import { RefreshCw, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePwaUpdate } from "./usePwaUpdate";

/**
 * PwaUpdateBanner es la presentación pura del aviso «hay versión nueva»: una barra fija
 * abajo (visible en móvil, sin tapar el contenido) con la acción de recargar y un cierre.
 * Se separa del hook para poder probarla sin el service worker.
 */
export function PwaUpdateBanner({
  onUpdate,
  onDismiss,
}: {
  onUpdate: () => void;
  onDismiss: () => void;
}) {
  return (
    <div
      role="alert"
      className="fixed inset-x-0 bottom-0 z-50 mx-auto flex w-full max-w-5xl items-center gap-3 border-slate-700 border-t bg-slate-900 px-4 py-3 text-white shadow-lg sm:bottom-4 sm:rounded-xl sm:border"
    >
      <RefreshCw className="h-5 w-5 shrink-0 text-primary-300" aria-hidden="true" />
      <p className="flex-1 font-medium text-sm">
        Nueva versión disponible.
        <span className="hidden sm:inline"> Recarga para actualizar.</span>
      </p>
      <Button size="sm" onClick={onUpdate}>
        Recargar
      </Button>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Descartar aviso de actualización"
        className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-800 hover:text-white"
      >
        <X className="h-5 w-5" aria-hidden="true" />
      </button>
    </div>
  );
}

/**
 * PwaUpdatePrompt conecta el estado del service worker (F4 #55) con el banner. Montado en
 * RootLayout, además de mostrar el aviso es quien registra el SW (usePwaUpdate). No renderiza
 * nada hasta que hay una versión nueva en espera.
 */
export function PwaUpdatePrompt() {
  const { needRefresh, update, dismiss } = usePwaUpdate();
  if (!needRefresh) {
    return null;
  }
  return <PwaUpdateBanner onUpdate={update} onDismiss={dismiss} />;
}
